import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AppFooter from "@/components/layout/AppFooter";
import OwnerSidebar from "@/components/navigation/OwnerSidebar";
import { toast } from "sonner";
import { Loader2, Mail, Send, Eye, EyeOff, CheckCircle } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [hasSettings, setHasSettings] = useState(false);

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/email`);
      if (response.data) {
        setSmtpHost(response.data.smtp_host);
        setSmtpPort(response.data.smtp_port);
        setSmtpUser(response.data.smtp_user);
        setFromEmail(response.data.from_email);
        setFromName(response.data.from_name);
        setHasSettings(true);
      }
    } catch (error) {
      console.error("Error fetching email settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();

    if (!smtpHost || !smtpPort || !smtpUser || !smtpPassword || !fromEmail || !fromName) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/settings/email`, {
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort),
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        from_email: fromEmail,
        from_name: fromName
      });
      setHasSettings(true);
      toast.success("E-Mail-Einstellungen gespeichert!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      await axios.post(`${API_URL}/api/settings/email/test`);
      toast.success("Test-E-Mail wurde gesendet! Überprüfe dein Postfach.");
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Senden der Test-E-Mail");
    } finally {
      setTesting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="settings-page">
      <OwnerSidebar user={user} onLogout={handleLogout} />

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-6 py-8 flex-1 w-full">
          <h1 className="font-heading text-2xl font-semibold text-[#212529] mb-6">Einstellungen</h1>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#17c671]" />
            </div>
          ) : (
            <div>
              <Card className="animate-fade-in border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#17c671]" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-[#212529]">E-Mail-Einstellungen</CardTitle>
                      <CardDescription className="text-gray-500">
                        Konfiguriere deinen SMTP-Server für E-Mail-Benachrichtigungen
                      </CardDescription>
                    </div>
                  </div>
                  {hasSettings && (
                    <div className="flex items-center gap-2 mt-4 px-3 py-2 bg-[#17c671]/10 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-[#17c671]" />
                      <span className="text-sm text-[#17c671]">E-Mail-Einstellungen konfiguriert</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSaveSettings} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="smtp-host" className="text-[#212529]">SMTP-Server</Label>
                        <Input
                          id="smtp-host"
                          placeholder="z.B. smtp.gmail.com"
                          value={smtpHost}
                          onChange={(e) => setSmtpHost(e.target.value)}
                          className="border-gray-300 focus:border-[#17c671]"
                          data-testid="smtp-host-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="smtp-port" className="text-[#212529]">Port</Label>
                        <Input
                          id="smtp-port"
                          type="number"
                          placeholder="587"
                          value={smtpPort}
                          onChange={(e) => setSmtpPort(e.target.value)}
                          className="border-gray-300 focus:border-[#17c671]"
                          data-testid="smtp-port-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="smtp-user" className="text-[#212529]">Benutzername</Label>
                        <Input
                          id="smtp-user"
                          placeholder="deine@email.de"
                          value={smtpUser}
                          onChange={(e) => setSmtpUser(e.target.value)}
                          className="border-gray-300 focus:border-[#17c671]"
                          data-testid="smtp-user-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="smtp-password" className="text-[#212529]">Passwort</Label>
                        <div className="relative">
                          <Input
                            id="smtp-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={smtpPassword}
                            onChange={(e) => setSmtpPassword(e.target.value)}
                            className="pr-10 border-gray-300 focus:border-[#17c671]"
                            data-testid="smtp-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <p className="text-sm text-gray-500 mb-4">Absender-Informationen</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="from-email" className="text-[#212529]">Absender-E-Mail</Label>
                          <Input
                            id="from-email"
                            type="email"
                            placeholder="noreply@firma.de"
                            value={fromEmail}
                            onChange={(e) => setFromEmail(e.target.value)}
                            className="border-gray-300 focus:border-[#17c671]"
                            data-testid="from-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="from-name" className="text-[#212529]">Absender-Name</Label>
                          <Input
                            id="from-name"
                            placeholder="z.B. smartdienstplan"
                            value={fromName}
                            onChange={(e) => setFromName(e.target.value)}
                            className="border-gray-300 focus:border-[#17c671]"
                            data-testid="from-name-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <Button
                        type="submit"
                        className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold"
                        disabled={saving}
                        data-testid="save-settings-btn"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Speichern...
                          </>
                        ) : (
                          "Einstellungen speichern"
                        )}
                      </Button>

                      {hasSettings && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleTestEmail}
                          disabled={testing}
                          className="border-gray-300"
                          data-testid="test-email-btn"
                        >
                          {testing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Senden...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Test-E-Mail senden
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              <Card className="mt-6 animate-fade-in stagger-2 border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-[#212529]">SMTP-Hinweise</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600 space-y-3">
                  <p><strong>Gmail:</strong> Verwende smtp.gmail.com:587 und ein App-Passwort (nicht dein normales Passwort).</p>
                  <p><strong>Outlook/Office 365:</strong> Verwende smtp.office365.com:587.</p>
                  <p><strong>Eigener Server:</strong> Frage deinen Administrator nach den korrekten SMTP-Einstellungen.</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      <AppFooter />
    </div>
  );
};

export default SettingsPage;
